import { ModalController } from '@ionic/angular';

import { resolveProsegurTerminal, resolveProsegurTerminals, isPaymentTerminalManual, isPaytefTerminal, resolvePaymentTerminals, pickPaymentTerminal } from '@app/components/model/ticket/ui-paytef/ui-paytef-utils';
import { UiPickTerminalModal } from '@app/components/model/ticket/ui-paytef/ui-pickterminal-modal';

import { deviceService } from '@app/modules/sockets';
import { cashlessRefundStore } from '@app/components/model/ticket/ui-cashguard-cash/cashless-refund-store';
import { UiCashGuardCashPayModal } from '@app/components/model/ticket/ui-cashguard-cash/ui-cashguard-cash';

/**
 * Card payment "por el datafono del cajon" (cashless), used by ui-view and
 * ui-edit (ui-select mantiene su propio flujo cashless, equivalente).
 *
 * When the ticket's smart drawer exposes an integrated card terminal, the
 * charge is routed to it through the drawer modal instead of a standalone
 * paytef pinpad. El front es **agnostico**: siempre manda `CHARGE_CASHLESS`, y
 * cada driver del back lo traduce a su datafono:
 *
 *   - **PROSEGUR**: prosegur.rs traduce `CHARGE_CASHLESS` a un `Sale` del MDW
 *     con `deviceId="PaymentTerminal"` (el Middleware lo enruta al datafono). No
 *     hay probe: el datafono se asume presente.
 *   - **CASHLOGY** (WIPAY / ConnectorPlus): se sondea una vez con
 *     `GET_PERIPHERALS` y solo se enruta si hay un cashless `AVAILABLE`.
 */

/** `{ drawer, till, tolocal }` as returned by `place.smartdrawerForTicket`. */
export interface CashlessDrawerInfo {
    drawer: any;
    till: any;
    tolocal: boolean;
}

/** Per-component-independent cache of "this till has a cashless terminal". */
const _cashlessCache = new Map<string, boolean>();

/** Drawer models whose integrated datafono can charge a card. */
export function isCashlessCardDrawer(drawer: any): boolean {
    let _m = drawer?.model;
    return _m === 'PROSEGUR' || _m === 'CASHLOGY';
}

/**
 * Datáfonos PAYTEF activos de TODO el place, deduplicados por host/nombre.
 * resolvePaymentTerminals SOLO mira place.till; aquí barremos además todos los
 * servicios (place.possvc) para cubrir el caso de datáfonos dados de alta en un
 * till/cajón distinto al de place.till (setup "dos cajones": uno PROSEGUR con su
 * datáfono cashless y otro con el datáfono PAYTEF por IP). Sin esto la lista de
 * paytef salía vacía y el cobro iba directo a Prosegur sin ofrecer el selector.
 */
export function collectPaytefTerminals(place: any, view: any): Array<any> {
    let _list = (resolvePaymentTerminals(place, view) || []).slice();
    let _seen = new Set(_list.map(t => (t && (t.host || t.name)) || t));
    for (let _svc of (place?.possvc || [])){
        for (let _t of (_svc?.payment_terminals || [])){
            if (_t && _t.status === 'AC' && isPaytefTerminal(_t)){
                let _key = (_t.host || _t.name) || _t;
                if (!_seen.has(_key)){ _seen.add(_key); _list.push(_t); }
            }
        }
    }
    return _list;
}

/** Elección del cajero en el selector de datáfono (o 'default' = sin selector). */
export type CardChoice =
    | { kind: 'paytef', terminal: any }
    | { kind: 'prosegur', terminal: any }
    | { kind: 'manual' }
    | { kind: 'cancelled' }
    | { kind: 'default' };

/**
 * Selector de datáfono UNIFICADO para el cobro con tarjeta. Reúne los datáfonos
 * PROSEGUR (cashless del cajón, vía CHARGE_CASHLESS/MDW) y PAYTEF (por IP) de
 * TODO el place; si existen de AMBOS tipos, deja ELEGIR al cajero y devuelve su
 * elección. Con un solo tipo (o ninguno) devuelve 'default' y el caller sigue su
 * flujo de siempre, sin cambios de comportamiento.
 *
 * Se comparte entre TODOS los OnPaycard (ui-view, ui-edit, ui-thumb, tab-select,
 * panel-payment) para que el selector aparezca sea cual sea el botón de "tarjeta"
 * que se pulse — antes solo lo tenía ui-view y por eso el TPV (tab-select) iba
 * directo a Prosegur sin ofrecerlo.
 */
export async function chooseCardTerminal(modalCtrl: ModalController, lang: any, place: any, view: any, cgInfo: any): Promise<CardChoice> {
    let _prosegurs = (cgInfo && isCashlessCardDrawer(cgInfo.drawer)) ? (resolveProsegurTerminals(cgInfo.till) || []) : [];
    let _paytefs = collectPaytefTerminals(place, view);

    // Solo hay ambigüedad de TIPO —y por tanto selector combinado— si existen
    // datáfonos de AMBOS tipos. Con un solo tipo (o ninguno) el caller sigue su
    // flujo normal ('default')... con UNA excepción (abajo).
    if (_prosegurs.length === 0 || _paytefs.length === 0){
        // Excepción: sin PROSEGUR pero con paytefs que collectPaytefTerminals
        // descubrió FUERA de place.till (setup dos-cajones). El fallback del
        // caller (resolvePaymentTerminals) solo mira place.till, así que esos
        // datáfonos se perderían y el cobro caería a manual / "éxito" sin cobrar.
        // Solo en ESE caso enrutamos el paytef aquí; si no hay extras, 'default'
        // y todo sigue EXACTAMENTE igual (no-op en el flujo de siempre).
        // En modo datáfono el dispositivo usa su propio flujo local: no tocamos.
        if (_prosegurs.length === 0 && _paytefs.length > 0 && view?.IsDatafono !== true){
            let _own = (resolvePaymentTerminals(place, view) || []);
            let _ownKeys = new Set(_own.map(t => (t && (t.host || t.name)) || t));
            let _hasExtra = _paytefs.some(t => !_ownKeys.has((t && (t.host || t.name)) || t));
            if (_hasExtra){
                let _pick = await pickPaymentTerminal(modalCtrl, lang, _paytefs, view);
                if (!_pick){ return { kind: 'cancelled' }; }
                if (isPaymentTerminalManual(_pick)){ return { kind: 'manual' }; }
                return { kind: 'paytef', terminal: _pick };
            }
        }
        return { kind: 'default' };
    }

    // Modo datáfono: la tarjeta va por el LECTOR local (paytef). El cajón
    // Prosegur no se ofrece: este dispositivo no tiene por qué alcanzarlo.
    if (view?.IsDatafono){
        let _pick = await pickPaymentTerminal(modalCtrl, lang, _paytefs, view);
        if (!_pick){ return { kind: 'cancelled' }; }
        if (isPaymentTerminalManual(_pick)){ return { kind: 'manual' }; }
        return { kind: 'paytef', terminal: _pick };
    }

    let _pick = await pickPaymentTerminal(modalCtrl, lang, [ ..._prosegurs, ..._paytefs ], view);
    if (!_pick){ return { kind: 'cancelled' }; }
    if (isPaymentTerminalManual(_pick)){ return { kind: 'manual' }; }
    if ((_pick.model || '').toString().toUpperCase() === 'PROSEGUR'){ return { kind: 'prosegur', terminal: _pick }; }
    return { kind: 'paytef', terminal: _pick };
}

/** Extracts whether any cashless peripheral is `AVAILABLE` from a
 *  `GET_PERIPHERALS` payload (`{peripherals:{cashless:[...]}}`). */
function parseCashlessAvailable(data: any): boolean {
    let _d = data;
    if (typeof _d === 'string'){
        try { _d = JSON.parse(_d); } catch(_e){ return false; }
    }
    let _list = _d?.peripherals?.cashless;
    if (!Array.isArray(_list)){
        return false;
    }
    return _list.some(_p => {
        let _state = (_p?.state || _p?.status || '').toString().toUpperCase();
        return _state === 'AVAILABLE';
    });
}

/**
 * Is the ticket's drawer able to charge a card through its datafono right now?
 * PROSEGUR → always (MDW-managed). CASHLOGY → probe `GET_PERIPHERALS` (cached).
 * Anything else → false.
 */
export async function cashlessCardAvailable(devsvc: deviceService, cgInfo: CashlessDrawerInfo): Promise<boolean> {
    let _drawer = cgInfo?.drawer || null;
    let _till = cgInfo?.till || null;
    if (!_drawer || !_till){
        return false;
    }
    if (_drawer.model === 'PROSEGUR'){
        // El MDW no expone GET_PERIPHERALS, asi que el datafono se declara en
        // la configuracion: un PAYTERMINAL con modelo PROSEGUR en este till.
        if (resolveProsegurTerminal(_till)){
            return true;
        }

        // Sin datafono PROSEGUR dado de alta NO se cobra por el MDW: la tarjeta
        // cae al flujo normal de datafono -> paytef si hay terminal configurado,
        // o tarjeta manual si no hay ninguno. (Antes, sin terminales configurados
        // se ASUMIA que el datafono Prosegur existia y se cobraba por el MDW igual;
        // eso impedia usar el datafono del banco en instalaciones sin datafono
        // Prosegur real. Ahora el MDW solo se usa con un PAYTERMINAL PROSEGUR.)
        console.warn('[CASHLESS] cajon PROSEGUR sin datafono PROSEGUR dado de alta en el till',
                     _till?.name, '-> la tarjeta va a paytef/banco (o manual).',
                     'Da de alta un datafono con modelo PROSEGUR para cobrar por el MDW.');
        return false;
    }
    if (_drawer.model !== 'CASHLOGY'){
        return false;
    }

    let _key = _till.name;
    if (_cashlessCache.has(_key)){
        return _cashlessCache.get(_key);
    }

    let _available = false;
    let _conclusive = false;
    let _connection = null;
    try {
        let _host = _till.wshost;
        let _port = _till.wsport ? parseInt(_till.wsport) : 5005;
        _connection = await devsvc.Connect(_host, _port, _till.name, cgInfo.tolocal);
        if (_connection){
            let _resp = await Promise.race([
                _connection.Send(_drawer.model, { command: 'GET_PERIPHERALS' }),
                new Promise<null>((r) => setTimeout(() => r(null), 8000))
            ]);
            if (_resp){
                _available = parseCashlessAvailable(_resp.data);
                _conclusive = true;
            }
        }
    }
    catch(e){
        console.warn('[CASHLESS] GET_PERIPHERALS check failed', e);
    }
    finally {
        if (_connection){
            devsvc.Disconnect(_connection);
        }
    }

    // Cache only a conclusive `true` (a cashless AVAILABLE persists); a `false`
    // is not cached so the datafono can come up later (login WIPAY / reconnect)
    // without leaving cashless dead for the session.
    if (_conclusive && _available){
        _cashlessCache.set(_key, true);
    }
    return _available;
}

/** Component hooks the cashless modal needs that are not globally available. */
export interface CashlessModalDeps {
    modalCtrl: ModalController;
    /** Remember the datafono transactionNumber for a later cashless refund. */
    cashlessStore?: cashlessRefundStore;
    /** `place.CardModal` — whether a manual-card fallback modal exists. */
    hasCardModal: boolean;
    /** Opens the manual-card modal (the caller's own `_openManualCardModal`). */
    openManual: (tickets: any[]) => Promise<boolean>;
    /** Explicit amount for a mixed / partial card leg (euros), optional. */
    amountDue?: number;
    /**
     * Se invoca EN CUANTO la modal cashless se cierra (al inicio del
     * onDidDismiss), ANTES del posible fallback a `openManual`. El caller lo usa
     * para liberar su guard anti-doble-toque (`_waitmodal`): si no lo liberara
     * aqui, `openManual` — que corre dentro de este mismo onDidDismiss — veria el
     * guard aun en true y se auto-bloquearia, dejando el pago sin cobrar.
     */
    onModalDismissed?: () => void;
    /**
     * Datáfono PROSEGUR ya elegido por el caller (p.ej. en un selector combinado
     * PROSEGUR+paytef). Si viene, el flujo cashless lo usa directamente y NO
     * vuelve a pedir terminal.
     */
    chosenTerminal?: any;
}

/**
 * Opens the drawer modal in cashless mode (charges the card through the
 * datafono). Resolves `true` if paid, `false` on failure/cancel. On the modal's
 * "cobrar manual" fallback it delegates to `openManual` (or aborts if there is
 * no manual-card modal, to avoid registering a phantom charge).
 */
export async function openCashlessCardModal(deps: CashlessModalDeps, tickets: any[], cgInfo: CashlessDrawerInfo): Promise<boolean> {
    let _ticket = tickets[0];
    return new Promise<boolean>(async (resolve) => {
        try {
            let _props: any = {
                tickets: tickets,
                device: cgInfo.drawer,
                place: cgInfo.till,
                tolocal: cgInfo.tolocal,
                cashless: true
            };
            // PROSEGUR: el `host` del datafono es el alias con el que el MDW lo
            // identifica (PaymentTerminal, PaymentTerminal2...). Es lo que
            // encamina el cobro cuando hay mas de un datafono. Si no hay
            // terminal dado de alta (instalacion antigua) no mandamos nada y
            // prosegur.rs aplica su PaymentTerminal por defecto.
            //
            // Con 2+ datafonos PROSEGUR dejamos ELEGIR al cajero (mismo selector
            // que paytef); con 1 o 0 se mantiene el comportamiento de antes.
            let _prosegurs = resolveProsegurTerminals(cgInfo.till);
            // Datáfono ya elegido en un selector combinado (PROSEGUR+paytef): usarlo
            // directamente, sin volver a pedir terminal aquí.
            let _prosegur = deps.chosenTerminal || null;
            if (!_prosegur && _prosegurs.length >= 2){
                let _pickModal = await deps.modalCtrl.create({
                    component: UiPickTerminalModal,
                    cssClass: 'ticket-modal',
                    componentProps: { terminals: _prosegurs }
                });
                await _pickModal.present();
                let { data: _pick } = await _pickModal.onDidDismiss();
                if (!_pick){
                    // Selector cancelado: no se cobra (liberar el guard del caller).
                    if (deps.onModalDismissed){ deps.onModalDismissed(); }
                    resolve(false);
                    return;
                }
                if (isPaymentTerminalManual(_pick)){
                    // "Cobrar manual": cae al modal de tarjeta manual.
                    if (deps.onModalDismissed){ deps.onModalDismissed(); }
                    if (!deps.hasCardModal){ resolve(false); return; }
                    resolve(await deps.openManual(tickets));
                    return;
                }
                _prosegur = _pick;
            }
            else if (!_prosegur){
                _prosegur = _prosegurs[0] || resolveProsegurTerminal(cgInfo.till);
            }
            if (_prosegur && _prosegur.host){
                _props.deviceId = _prosegur.host;
            }
            if (typeof deps.amountDue === 'number' && deps.amountDue > 0){
                _props.amountDue = deps.amountDue;
            }
            let modal = await deps.modalCtrl.create({
                component: UiCashGuardCashPayModal,
                cssClass: 'modal-cashguard',
                backdropDismiss: false,
                componentProps: _props
            });

            modal.onDidDismiss().then(async (detail) => {
                // La modal cashless se cerro: avisar YA (antes del fallback) para
                // que el caller libere su guard _waitmodal; si no, el
                // `openManual` de mas abajo — que corre en este mismo handler —
                // se auto-bloquearia con el guard aun en true.
                if (deps.onModalDismissed){
                    deps.onModalDismissed();
                }
                // try/catch: si `openManual` (u otra cosa) lanza, el caller
                // recibe `false` en vez de dejar la Promise colgada.
                try {
                    // Cashless charge failed -> "cobrar manual" falls back to
                    // the manual-card modal (it is a card payment, not cash).
                    if (detail.data && detail.data['fallback']){
                        if (!deps.hasCardModal){
                            resolve(false);
                            return;
                        }
                        resolve(await deps.openManual(tickets));
                        return;
                    }
                    // Success: cache la referencia del cobro para el abono
                    // posterior. CASHLOGY -> transactionNumber (cashlessInfo);
                    // PROSEGUR -> orderId (MDW). Se cachea si hay cualquiera.
                    let _ci = detail.data ? detail.data['cashlessInfo'] : null;
                    let _orderId = detail.data ? detail.data['orderId'] : null;
                    let _txn = _ci && _ci.transactionNumber ? String(_ci.transactionNumber) : null;
                    if (deps.cashlessStore && (_txn || _orderId)){
                        let _entry = {
                            transaction_number: _txn,
                            order_id: _orderId ? String(_orderId) : null,
                            peripheral_id: _ci && _ci.peripheralId ? String(_ci.peripheralId) : null,
                            // PROSEGUR: datafono del cobro, para que el abono
                            // (CardRefund) vaya al MISMO con 2+ datafonos.
                            device_id: _prosegur && _prosegur.host ? String(_prosegur.host) : null,
                            amount: Math.round((detail.data['amount'] || 0) * 100),
                            created_at: Math.floor(Date.now() / 1000)
                        };
                        // Un cobro puede cubrir VARIOS tickets (guest paga
                        // Selected en un solo cargo). Indexar la referencia bajo
                        // el orderno de CADA ticket para que el abono posterior la
                        // encuentre desde cualquiera (antes solo tickets[0], y los
                        // demas caian a manual). Los callers de 1 ticket
                        // (ui-view/ui-edit) no cambian.
                        for (let _tk of tickets){
                            if (!_tk){ continue; }
                            // Key ESTABLE = `_uuid` (existe al cobrar; el orderno se
                            // asigna después, en el commit). Si es una COPIA de
                            // edición (ui-edit), el `_uuid` de la copia y el del
                            // ticket CANÓNICO (CopyOf) son distintos, y la devolución
                            // posterior consulta el canónico → se cachea bajo AMBOS.
                            let _keys = [ (_tk as any)._uuid ];
                            let _canon: any = (_tk as any).CopyOf;
                            if (_canon && _canon._uuid && _canon._uuid !== (_tk as any)._uuid){
                                _keys.push(_canon._uuid);
                            }
                            for (let _k of _keys){
                                if (_k){ deps.cashlessStore.remember(_k, { ..._entry }); }
                            }
                        }
                    }
                    resolve(detail.data != null);
                } catch(e){
                    console.error('[CASHLESS] dismiss handler error', e);
                    resolve(false);
                }
            });

            await modal.present();
        } catch(e){
            console.error('[CASHLESS] modal error', e);
            resolve(false);
        }
    });
}

/**
 * Convenience: try to charge a card through the ticket's drawer datafono.
 *
 * Returns:
 *   - `null`  → this drawer cannot charge cards (caller falls through to its
 *               normal paytef / manual-card flow).
 *   - `true`  → charged successfully.
 *   - `false` → attempted but failed/cancelled (do NOT fall through to paytef).
 */
export async function tryCashlessCardPayment(devsvc: deviceService, deps: CashlessModalDeps, tickets: any[], cgInfo: CashlessDrawerInfo): Promise<boolean | null> {
    if (!cgInfo?.drawer || !isCashlessCardDrawer(cgInfo.drawer)){
        return null;
    }
    if (!(await cashlessCardAvailable(devsvc, cgInfo))){
        return null;
    }
    return openCashlessCardModal(deps, tickets, cgInfo);
}
